/* Audit Assistant — a single chat bubble.
 *
 * User: right-aligned accent bubble. Assistant: left-aligned dark glass
 * card with avatar, timestamp, and Copy / Regenerate actions.
 *
 * Assistant text uses a tiny, XSS-safe formatter (builds React nodes,
 * never dangerouslySetInnerHTML): `**bold**` → <strong>, lines starting
 * with "• " → bullet rows, blank lines → spacing. Content is fully
 * controlled mock data; user text is rendered verbatim.
 */

import { useState } from 'react';
import type { JSX } from 'react';
import type { ChatMessage } from '../../hooks/useMockChat';

function renderBold(line: string, keyBase: string): JSX.Element[] {
  // Split on **...** and wrap those segments in <strong>.
  const parts = line.split(/(\*\*[^*]+\*\*)/g).filter((s) => s !== '');
  return parts.map((seg, i) => {
    if (seg.startsWith('**') && seg.endsWith('**')) {
      return <strong key={`${keyBase}-b${i}`}>{seg.slice(2, -2)}</strong>;
    }
    return <span key={`${keyBase}-t${i}`}>{seg}</span>;
  });
}

function FormattedText({ text }: { text: string }): JSX.Element {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, i) => {
        if (line.trim() === '') return <div key={`sp-${i}`} className="aa-line-gap" />;
        if (line.startsWith('• ')) {
          return (
            <div key={`li-${i}`} className="aa-bullet">
              <span className="aa-bullet-dot" aria-hidden="true">•</span>
              <span>{renderBold(line.slice(2), `li-${i}`)}</span>
            </div>
          );
        }
        return (
          <div key={`ln-${i}`} className="aa-line">
            {renderBold(line, `ln-${i}`)}
          </div>
        );
      })}
    </>
  );
}

function formatTime(ts: number): string {
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

interface ChatBubbleProps {
  message: ChatMessage;
  onRegenerate?: (id: string) => void;
  canRegenerate?: boolean;
}

export function ChatBubble({ message, onRegenerate, canRegenerate }: ChatBubbleProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';

  const copy = (): void => {
    try {
      void navigator.clipboard?.writeText(message.text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  if (isUser) {
    return (
      <div className="aa-msg aa-msg-user">
        <div className="aa-bubble aa-bubble-user">
          <div className="aa-bubble-body">{message.text}</div>
          <div className="aa-meta aa-meta-user">{formatTime(message.ts)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="aa-msg aa-msg-assistant">
      <div className="aa-avatar" aria-hidden="true">AI</div>
      <div className="aa-bubble aa-bubble-assistant">
        <div className="aa-bubble-body">
          <FormattedText text={message.text} />
        </div>
        <div className="aa-meta">
          <span>{formatTime(message.ts)}</span>
          <span className="aa-meta-actions">
            <button type="button" className="aa-icon-btn" onClick={copy} data-tip="Copy response">
              {copied ? 'Copied' : 'Copy'}
            </button>
            {canRegenerate && onRegenerate && (
              <button
                type="button"
                className="aa-icon-btn"
                onClick={() => onRegenerate(message.id)}
                data-tip="Regenerate response"
              >
                Regenerate
              </button>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
